import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import { useAuth } from './AuthContext';
import { useBottomNavTarget } from '../lib/useBottomNavTarget';

const NotificationsContext = createContext(null);

/* The bell badge lives in GlobalNav while the feed that clears it lives in
   NotificationsPage, so the unread count is held here and kept in sync from
   both sides. It tracks the same condo the bell links to, resolved from the
   user's own condos — an admin browsing a condo they aren't a member of gets
   no badge, only the page itself. */
const POLL_INTERVAL_MS = 60000;

export function NotificationsProvider({ children }) {
  const { getToken, isAuthenticated, user } = useAuth();
  const { slug } = useBottomNavTarget();
  const [unreadCount, setUnreadCount] = useState(0);
  /* Every count write bumps this, and a fetch drops its result if the counter
     moved while it was in flight — so a slow count for the condo the user just
     navigated away from can't overwrite the condo they are on now, and neither
     can a poll that lands right after a row was cleared. */
  const writeSeq = useRef(0);

  const condoId = useMemo(() => {
    if (!slug) return null;
    const match = (user?.condos || []).find((c) => c?.slug === slug);
    return match?._id || null;
  }, [user, slug]);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken]
  );

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !condoId) {
      writeSeq.current += 1;
      setUnreadCount(0);
      return;
    }

    const seq = writeSeq.current;
    try {
      const response = await axios.get(
        `${getApiBaseUrl()}/api/notifications/condo/${condoId}/unread-count`,
        authConfig()
      );
      if (seq !== writeSeq.current) return;
      setUnreadCount(response.data.unreadCount || 0);
    } catch (err) {
      console.error('Failed to fetch unread notification count:', err);
    }
  }, [isAuthenticated, condoId, authConfig]);

  // A condo change supersedes whatever count is still in flight for the old one.
  useEffect(() => {
    writeSeq.current += 1;
  }, [condoId]);

  useEffect(() => {
    refresh();
    if (!condoId) return undefined;
    const timer = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [refresh, condoId]);

  // Callers only mark rows that were still unread, so the badge can drop by one
  // without re-counting the whole feed.
  const markRead = useCallback(async (notificationId) => {
    writeSeq.current += 1;
    setUnreadCount((count) => Math.max(0, count - 1));
    try {
      await axios.post(
        `${getApiBaseUrl()}/api/notifications/${notificationId}/read`,
        {},
        authConfig()
      );
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      refresh();
    }
  }, [authConfig, refresh]);

  const markAllRead = useCallback(async (targetCondoId) => {
    writeSeq.current += 1;
    setUnreadCount(0);
    try {
      await axios.post(
        `${getApiBaseUrl()}/api/notifications/condo/${targetCondoId}/read-all`,
        {},
        authConfig()
      );
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      refresh();
      throw err;
    }
  }, [authConfig, refresh]);

  const value = useMemo(
    () => ({ unreadCount, refresh, markRead, markAllRead }),
    [unreadCount, refresh, markRead, markAllRead]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}
